"use client"

import * as React from "react"
import {
  IconArrowRightDashed,
  IconDeviceLaptop,
  IconMoon,
  IconSun,
  IconBriefcase,
  IconPlus,
  IconUsers,
  IconReceipt,
  IconLoader2
} from "@tabler/icons-react"
import { useTheme } from "next-themes"
import { useRouter } from "next/navigation"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import { sidebarData } from "./layout/data/sidebar-data"
import { useSearch } from "./search-provider"
import { ScrollArea } from "./ui/scroll-area"

type Project = {
  id: string
  name: string
  referenceNumber: string
  status: string
}

export function CommandMenu() {
  const router = useRouter()
  const { setTheme } = useTheme()
  const { open, setOpen } = useSearch()
  const [projects, setProjects] = React.useState<Project[]>([])
  const [loading, setLoading] = React.useState(false)
  const [query, setQuery] = React.useState("")

  // Fetch projects when dialog opens
  React.useEffect(() => {
    if (open && projects.length === 0) {
      setLoading(true)
      fetch('/api/projects')
        .then(res => res.json())
        .then(data => setProjects(data.projects || []))
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [open, projects.length])

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false)
      command()
    },
    [setOpen]
  )

  // Filter projects based on query
  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.referenceNumber.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 5)

  return (
    <CommandDialog modal open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Type a command or search projects..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <ScrollArea type="hover" className="h-72 pr-1">
          <CommandEmpty>No results found.</CommandEmpty>

          {/* Quick Actions */}
          <CommandGroup heading="Quick Actions">
            <CommandItem onSelect={() => runCommand(() => router.push('/costing'))}>
              <IconPlus className="mr-2 h-4 w-4" />
              Create New Quote
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.push('/projects'))}>
              <IconBriefcase className="mr-2 h-4 w-4" />
              View All Bookings
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.push('/suppliers'))}>
              <IconUsers className="mr-2 h-4 w-4" />
              Manage Suppliers
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => router.push('/finance'))}>
              <IconReceipt className="mr-2 h-4 w-4" />
              Finance Center
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          {/* Bookings Search */}
          {filteredProjects.length > 0 && (
            <CommandGroup heading="Bookings">
              {filteredProjects.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`${project.name} ${project.referenceNumber}`}
                  onSelect={() => runCommand(() => router.push(`/bookings/${project.id}`))}
                >
                  <IconBriefcase className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>{project.name}</span>
                    <span className="text-xs text-muted-foreground">{project.referenceNumber}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {loading && (
            <CommandGroup heading="Bookings">
              <CommandItem disabled>
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading bookings...
              </CommandItem>
            </CommandGroup>
          )}

          <CommandSeparator />

          {/* Navigation */}
          {sidebarData.navGroups.map((group) => (
            <CommandGroup key={group.title} heading={group.title}>
              {group.items.map((navItem, i) => {
                if (navItem.url)
                  return (
                    <CommandItem
                      key={`${navItem.url}-${i}`}
                      value={navItem.title}
                      onSelect={() => {
                        runCommand(() => router.push(navItem.url))
                      }}
                    >
                      <div className="mr-2 flex h-4 w-4 items-center justify-center">
                        <IconArrowRightDashed className="text-muted-foreground/80 size-2" />
                      </div>
                      {navItem.title}
                    </CommandItem>
                  )

                return navItem.items?.map((subItem, i) => (
                  <CommandItem
                    key={`${subItem.url}-${i}`}
                    value={subItem.title}
                    onSelect={() => {
                      runCommand(() => router.push(subItem.url))
                    }}
                  >
                    <div className="mr-2 flex h-4 w-4 items-center justify-center">
                      <IconArrowRightDashed className="text-muted-foreground/80 size-2" />
                    </div>
                    {subItem.title}
                  </CommandItem>
                ))
              })}
            </CommandGroup>
          ))}
          <CommandSeparator />
          <CommandGroup heading="Theme">
            <CommandItem onSelect={() => runCommand(() => setTheme("light"))}>
              <IconSun /> <span>Light</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme("dark"))}>
              <IconMoon className="scale-90" />
              <span>Dark</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme("system"))}>
              <IconDeviceLaptop />
              <span>System</span>
            </CommandItem>
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </CommandDialog>
  )
}

